import React, { createContext, useContext, useState, useCallback } from 'react';
import { Toast } from './Toast';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'default', duration = 4000) => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      message,
      type,
      duration,
    };

    setToasts((prev) => [...prev, newToast]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (message, duration = 4000) => {
      return addToast(message, 'default', duration);
    },
    [addToast]
  );

  const showError = useCallback(
    (message, duration = 4000) => {
      return addToast(message, 'error', duration);
    },
    [addToast]
  );

  const showSuccess = useCallback(
    (message, duration = 4000) => {
      return addToast(message, 'success', duration);
    },
    [addToast]
  );

  const showWarning = useCallback(
    (message, duration = 4000) => {
      return addToast(message, 'warning', duration);
    },
    [addToast]
  );

  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  const value = {
    showToast,
    showError,
    showSuccess,
    showWarning,
    clearAll,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className='gigacore-toast-container'>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};
